import * as React from "react"
import { Head, Link, useForm, usePage } from "@inertiajs/react"
import { Download, Mail, Phone } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { AdminPageHeader, AdminSection, EmptyState } from "@/components/admin/AdminPageHeader"
import { PipelineBadge, StatusBadge } from "@/components/admin/Badges"
import { Field, fieldAria } from "@/components/admin/Field"
import { enquiryTitle, formatBytes, plural } from "@/components/admin/format"
import { MockupCard } from "@/components/admin/MockupCard"
import { ResponsiveList } from "@/components/admin/ResponsiveList"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { DataRow, DataTable } from "@/components/ui/data-table"
import { Radio, RadioGroup } from "@/components/ui/radio"
import { Select } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { formatDateOnly, formatDayTime } from "@/lib/dates"
import { formatPenceOr } from "@/lib/money"
import { cn } from "@/lib/utils"
import type {
  AdminEnquiryDetail,
  AdminEnquiryLine,
  AdminEnquiryShowProps,
  EnquiryPipeline,
  EnquiryStatus,
  Option,
  PageProps,
} from "@/types/inertia"

// One enquiry for Duncan (M7 decision 8): three small forms — the
// customer-visible status (mails the customer), won / lost and private notes —
// then everything the customer sent. Each form PATCHes one field; the others
// keep what is being typed (preserveState), and the page keeps its scroll.

export default function AdminEnquiryShow() {
  const { props } = usePage<PageProps<AdminEnquiryShowProps>>()
  const { enquiry, options } = props
  const title = enquiryTitle(enquiry)
  const description = `${title} from ${enquiry.name}: status, won or lost, private notes, contact details, wines, designs and history.`

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <AdminShell>
        <AdminPageHeader
          title={title}
          description={
            <>
              {enquiry.name}
              {enquiry.company && ` · ${enquiry.company}`} · received {formatDayTime(enquiry.created_at)}
            </>
          }
          back={{ href: "/admin/enquiries", label: "All enquiries" }}
          meta={
            <>
              <Badge tone="neutral">{enquiry.enquiry_type_label}</Badge>
              <StatusBadge status={enquiry.status} label={enquiry.status_label} />
              <PipelineBadge pipeline={enquiry.pipeline} label={enquiry.pipeline_label} />
            </>
          }
        />

        <AdminSection id="enquiry-manage" title="Manage">
          <div className="grid gap-4 md:grid-cols-2">
            <StatusForm enquiry={enquiry} statuses={options.statuses} />
            <PipelineForm enquiry={enquiry} pipelines={options.pipelines} />
            <NotesForm enquiry={enquiry} />
          </div>
        </AdminSection>

        <AdminSection id="enquiry-contact" title="Contact">
          <DataTable>
            <DataRow title="Name">{enquiry.name}</DataRow>
            <DataRow title="Company">{enquiry.company ?? "Not given"}</DataRow>
            <DataRow title="Email">
              <a href={enquiry.email_href} className="inline-flex min-h-11 items-center gap-2 break-all">
                <Mail className="h-4 w-4 shrink-0" aria-hidden="true" />
                {enquiry.email}
              </a>
            </DataRow>
            <DataRow title="Phone">
              {enquiry.phone && enquiry.phone_href ? (
                <a href={enquiry.phone_href} className="inline-flex min-h-11 items-center gap-2">
                  <Phone className="h-4 w-4 shrink-0" aria-hidden="true" />
                  {enquiry.phone}
                </a>
              ) : (
                (enquiry.phone ?? "Not given")
              )}
            </DataRow>
            <DataRow title="Account">
              {enquiry.account ? (
                <Link href={enquiry.account.url} className="inline-flex min-h-11 items-center">
                  {enquiry.account.name ? `${enquiry.account.name} · ${enquiry.account.email}` : enquiry.account.email}
                </Link>
              ) : (
                "Sent as a guest (no account yet)"
              )}
            </DataRow>
          </DataTable>
        </AdminSection>

        <AdminSection id="enquiry-event" title="The occasion">
          <DataTable>
            <DataRow title="Type">{enquiry.enquiry_type_label}</DataRow>
            <DataRow title="Occasion">{enquiry.event_type_label}</DataRow>
            <DataRow title="Event date">{enquiry.event_date ? formatDateOnly(enquiry.event_date) : "Not given"}</DataRow>
            <DataRow title="Bottles">
              {enquiry.bottle_count === null
                ? "Not given"
                : `${enquiry.enquiry_type === "consultation" ? "About " : ""}${plural(enquiry.bottle_count, "bottle")}`}
            </DataRow>
            {enquiry.enquiry_type === "order" && (
              <DataRow title="Estimate">
                {formatPenceOr(enquiry.estimate_pence, "Not available until every wine is priced")}
              </DataRow>
            )}
            <DataRow title="Received">{formatDayTime(enquiry.created_at)}</DataRow>
          </DataTable>
        </AdminSection>

        <AdminSection id="enquiry-message" title="Message">
          {enquiry.message ? (
            <div className="callout">
              <p className="whitespace-pre-wrap break-words">{enquiry.message}</p>
            </div>
          ) : (
            <EmptyState>No message.</EmptyState>
          )}
        </AdminSection>

        {enquiry.enquiry_type === "order" && (
          <AdminSection id="enquiry-lines" title="Wines">
            {enquiry.lines.length === 0 ? (
              <EmptyState>No wines on this order.</EmptyState>
            ) : (
              <>
                <ResponsiveList
                  label="Wines on this order"
                  items={enquiry.lines}
                  getKey={(line) => line.wine_id}
                  card={(line) => <LineCard line={line} />}
                  columns={[
                    {
                      key: "wine",
                      header: "Wine",
                      cell: (line) => (
                        <div className="min-w-0">
                          <div className="font-medium">
                            <Link href={line.wine_url}>{line.wine_name}</Link>
                          </div>
                          <div className="text-xs text-ink-muted">
                            {line.producer} · {line.wine_type_label}
                          </div>
                        </div>
                      ),
                    },
                    {
                      key: "bottles",
                      header: "Bottles",
                      className: "whitespace-nowrap text-right tabular-nums",
                      cell: (line) => line.bottle_count.toLocaleString("en-GB"),
                    },
                    {
                      key: "unit",
                      header: "Per bottle",
                      className: "whitespace-nowrap text-right tabular-nums",
                      cell: (line) => formatPenceOr(line.unit_price_pence, "No price"),
                    },
                    {
                      key: "total",
                      header: "Line total",
                      className: "whitespace-nowrap text-right tabular-nums",
                      cell: (line) => formatPenceOr(line.line_total_pence),
                    },
                  ]}
                />
                <OrderTotals enquiry={enquiry} />
              </>
            )}
          </AdminSection>
        )}

        <AdminSection id="enquiry-designs" title="Label designs">
          {enquiry.designs.length === 0 ? (
            <EmptyState>No studio designs came with this enquiry.</EmptyState>
          ) : (
            <ul aria-labelledby="enquiry-designs" className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {enquiry.designs.map((design) => (
                <li key={design.id}>
                  <MockupCard mockup={design} showEnquiry={false} />
                </li>
              ))}
            </ul>
          )}
        </AdminSection>

        <AdminSection id="enquiry-artwork" title="Artwork file">
          {enquiry.artwork ? (
            <div className="callout flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="break-all text-sm font-medium text-ink-display">{enquiry.artwork.filename}</div>
                <div className="text-xs text-ink-muted">
                  {formatBytes(enquiry.artwork.byte_size)} · {enquiry.artwork.content_type}
                </div>
              </div>
              <Button asChild variant="secondary" size="lg">
                <a href={enquiry.artwork.url} download>
                  <Download className="h-4 w-4" aria-hidden="true" />
                  Download<span className="sr-only"> {enquiry.artwork.filename}</span>
                </a>
              </Button>
            </div>
          ) : (
            <EmptyState>No artwork file was uploaded.</EmptyState>
          )}
        </AdminSection>

        <AdminSection id="enquiry-history" title="Status history" description="Oldest first. Every change emailed the customer.">
          <ol className="divide-y divide-hairline overflow-hidden rounded-md border border-hairline bg-page">
            {enquiry.status_history.map((row, index) => (
              <li key={`${row.at}-${index}`} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-4 py-3">
                <div className="flex items-center gap-2">
                  <StatusBadge status={row.status} label={row.label} />
                  <span className="text-xs text-ink-muted">
                    {row.kind === "created" ? "Enquiry received" : "Status changed"}
                  </span>
                </div>
                <span className="text-sm tabular-nums text-ink-body">{formatDayTime(row.at)}</span>
              </li>
            ))}
          </ol>
        </AdminSection>
      </AdminShell>
    </>
  )
}

/** A form card on the Manage block. */
function ManageCard({
  id,
  title,
  children,
  className,
}: {
  id: string
  title: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <section aria-labelledby={id} className={cn("rounded-lg border border-hairline bg-surface p-4", className)}>
      <h3 id={id}>{title}</h3>
      <div className="mt-3">{children}</div>
    </section>
  )
}

function StatusForm({ enquiry, statuses }: { enquiry: AdminEnquiryDetail; statuses: Option[] }) {
  const form = useForm<{ status: EnquiryStatus }>({ status: enquiry.status })
  const unchanged = form.data.status === enquiry.status

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    form.patch(enquiry.update_url, { preserveScroll: true })
  }

  return (
    <ManageCard id="manage-status" title="Update status">
      <form onSubmit={submit} className="space-y-4">
        <Field
          id="enquiry-status"
          label="Status the customer sees"
          error={form.errors.status}
          hint={`Shown on their order page. Changing it emails ${enquiry.email}.`}
        >
          <Select
            {...fieldAria("enquiry-status", { error: form.errors.status, hint: true })}
            className="form-control-large"
            value={form.data.status}
            onChange={(event) => form.setData("status", event.target.value as EnquiryStatus)}
          >
            {statuses.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Field>
        <Button type="submit" size="lg" disabled={unchanged || form.processing}>
          {form.processing ? "Updating…" : "Update status"}
        </Button>
      </form>
    </ManageCard>
  )
}

function PipelineForm({ enquiry, pipelines }: { enquiry: AdminEnquiryDetail; pipelines: Option[] }) {
  const form = useForm<{ pipeline: EnquiryPipeline }>({ pipeline: enquiry.pipeline })
  const unchanged = form.data.pipeline === enquiry.pipeline

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    form.patch(enquiry.update_url, { preserveScroll: true })
  }

  return (
    <ManageCard id="manage-pipeline" title="Won / lost">
      <form onSubmit={submit} className="space-y-4">
        <fieldset className="space-y-1" aria-describedby="enquiry-pipeline-hint">
          <legend>Where this stands</legend>
          <p id="enquiry-pipeline-hint" className="text-xs text-ink-muted">
            Yours alone: never shown to the customer and never emailed.
          </p>
          <RadioGroup className="gap-0">
            {pipelines.map((option) => (
              <label
                key={option.value}
                className="flex min-h-11 cursor-pointer items-center gap-3 text-sm font-normal text-ink-body"
              >
                <Radio
                  name="enquiry-pipeline"
                  value={option.value}
                  checked={form.data.pipeline === option.value}
                  onChange={() => form.setData("pipeline", option.value as EnquiryPipeline)}
                />
                {option.label}
              </label>
            ))}
          </RadioGroup>
          {form.errors.pipeline && <p className="text-xs text-danger-display">{form.errors.pipeline}</p>}
        </fieldset>
        <Button type="submit" size="lg" disabled={unchanged || form.processing}>
          {form.processing ? "Saving…" : "Save"}
        </Button>
      </form>
    </ManageCard>
  )
}

function NotesForm({ enquiry }: { enquiry: AdminEnquiryDetail }) {
  const form = useForm<{ internal_notes: string }>({ internal_notes: enquiry.internal_notes })

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    form.patch(enquiry.update_url, { preserveScroll: true })
  }

  return (
    <ManageCard id="manage-notes" title="Internal notes" className="md:col-span-2">
      <form onSubmit={submit} className="space-y-4">
        <Field
          id="enquiry-internal-notes"
          label="Notes"
          error={form.errors.internal_notes}
          hint="Private to the admin: never shown to the customer and never emailed."
        >
          <Textarea
            {...fieldAria("enquiry-internal-notes", { error: form.errors.internal_notes, hint: true })}
            className="form-control-large"
            rows={5}
            value={form.data.internal_notes}
            onChange={(event) => form.setData("internal_notes", event.target.value)}
          />
        </Field>
        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" size="lg" disabled={!form.isDirty || form.processing}>
            {form.processing ? "Saving…" : "Save notes"}
          </Button>
          {form.isDirty && <span className="text-xs text-ink-muted">Unsaved changes</span>}
        </div>
      </form>
    </ManageCard>
  )
}

function LineCard({ line }: { line: AdminEnquiryLine }) {
  return (
    <div className="px-4 py-2">
      <div className="text-sm font-medium">
        <Link href={line.wine_url} className="inline-flex min-h-11 items-center">
          {line.wine_name}
        </Link>
      </div>
      <div className="text-xs text-ink-muted">
        {line.producer} · {line.wine_type_label}
      </div>
      <div className="mt-1 flex flex-wrap justify-between gap-x-4 text-sm tabular-nums text-ink-body">
        <span>
          {plural(line.bottle_count, "bottle")} × {formatPenceOr(line.unit_price_pence, "no price")}
        </span>
        <span className="text-ink-display">{formatPenceOr(line.line_total_pence)}</span>
      </div>
    </div>
  )
}

function OrderTotals({ enquiry }: { enquiry: AdminEnquiryDetail }) {
  return (
    <div className="mt-3 flex flex-wrap justify-between gap-x-6 gap-y-1 rounded-md border border-hairline bg-surface px-4 py-3 text-sm">
      <span className="text-ink-body">
        Total: <strong>{plural(enquiry.bottle_count ?? 0, "bottle")}</strong>
      </span>
      <span className="text-ink-body">
        Estimate:{" "}
        {enquiry.estimate_pence === null ? (
          "available once every wine is priced"
        ) : (
          <strong className="tabular-nums">{formatPenceOr(enquiry.estimate_pence)}</strong>
        )}
      </span>
    </div>
  )
}
